import {
    AiImageModel,
    GeneratedSlideModel,
    GenerateImagesRequest,
    GenerateImagesResponse,
    GeneratePresentationOutlineRequest,
    GeneratePresentationOutlineResponse,
    GenerateSlideNotesRequest,
    GenerateSlideNotesResponse,
    GenerateSlideRequest,
    GenerateWordCloudRequest,
    GenerateWordCloudResponse,
    ParsedWebPageTheme,
    RewriteTextRequest,
    RewriteTextResponse,
    TranslatePresentationRequest,
    TranslatePresentationResponse,
    TranslateSlideRequest,
    TranslateSlideResponse,
    TranslateTextRequest,
    TranslateTextResponse
} from "../../../common/aiConstants";
import { app } from "../../namespaces";
import Api from "../api";
import { ai as aiApi } from "../../../apis-v2/callables";
import * as apiErrors from "../../../apis-v2/_sdk/types/errors";
import { uploadBlobToSignedUrl } from "../utilities/uploadBlobToSignedUrl";

export class InsufficientCreditsError extends Error { }
export class TooManyRequestsError extends Error { }
export class UnprocessableContextError extends Error { }

export interface GeneratePresentationOutlineRequestWithFiles extends GeneratePresentationOutlineRequest {
    files?: File[];
}

export interface GenerateSlideRequestWithFiles extends GenerateSlideRequest {
    files?: File[];
}

export interface CollectionItemModel {
    text: {
        blocks: Record<string, any>[]
    },
    content_type?: string,
    content_value?: string
}

class AiGenerationService {
    private async _handleApiErrors<T>(call: () => Promise<T>): Promise<T> {
        try {
            return await call();
        } catch (err) {
            if (err instanceof apiErrors.PaymentRequiredError) {
                throw new InsufficientCreditsError(err.message);
            }
            if (err instanceof apiErrors.TooManyRequestsError) {
                throw new TooManyRequestsError(err.message);
            }
            if (err instanceof apiErrors.UnprocessableContentError) {
                throw new UnprocessableContextError(err.message);
            }

            throw err;
        }
    }

    private async _uploadFilesToTempAssetStorage(files: File[]): Promise<string[]> {
        return await Promise.all(files.map(async file => {
            const { writeUrl, fileName: rawFileName } = await Api.tempAssets.post({ extension: file.name.split(".").pop() });
            await uploadBlobToSignedUrl(file, writeUrl);
            return rawFileName;
        }));
    }

    public async getCreditsBalance(workspaceId) {
        const { balance } = await this._handleApiErrors(() => aiApi.getCreditsBalance({ workspaceId }));
        return balance;
    }

    public async generatePresentationOutline(
        request: GeneratePresentationOutlineRequestWithFiles
    ) {
        request.files = request.files ?? [];
        request.tempAssetFileNames = request.tempAssetFileNames ?? [];

        if (!request.indexFileName && request.files.length > 0) {
            const fileNames = await this._uploadFilesToTempAssetStorage(request.files);
            request.tempAssetFileNames.push(...fileNames);
        }
        delete request.files;

        const { response } = await this._handleApiErrors(() => aiApi.generatePresentationOutline({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as GeneratePresentationOutlineResponse;
    }

    public async generateSlide(
        request: GenerateSlideRequestWithFiles,
    ) {
        request.files = request.files ?? [];
        request.tempAssetFileNames = request.tempAssetFileNames ?? [];

        if (!request.indexFileName && request.files.length > 0) {
            const fileNames = await this._uploadFilesToTempAssetStorage(request.files);
            request.tempAssetFileNames.push(...fileNames);
        }
        delete request.files;

        const { response } = await this._handleApiErrors(() => aiApi.generateSlide({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as GeneratedSlideModel;
    }

    public async generateSlidePublic(
        request: GenerateSlideRequestWithFiles,
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.generateSlidePublic({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as GeneratedSlideModel;
    }

    public async generateWordCloud(
        request: Omit<GenerateWordCloudRequest, "traceData">
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.generateWordCloud({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as GenerateWordCloudResponse;
    }

    public async rewriteText(
        request: Omit<RewriteTextRequest, "traceData">
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.rewriteText({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as RewriteTextResponse;
    }

    public async generateSlideNotes(
        request: GenerateSlideNotesRequest
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.generateSlideNotes({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as GenerateSlideNotesResponse;
    }

    public async analyzeWebpage(url: string) {
        const { response } = await this._handleApiErrors(() => aiApi.analyzeWebpage({
            url,
            workspaceId: app.appController.workspaceId
        }));

        return response as ParsedWebPageTheme;
    }

    public async generateImages(
        request: Omit<GenerateImagesRequest, "traceData">
    ) {
        const { response, remainingCreditsBalance } = await this._handleApiErrors(() => aiApi.generateImages({
            ...request,
            model: request.model ?? app.user?.get("aiImageModel") ?? AiImageModel.IMAGEN_4_FAST,
            workspaceId: app.appController.workspaceId
        }));

        return { response, remainingCreditsBalance } as { response: GenerateImagesResponse, remainingCreditsBalance: number };
    }

    public async translateText(
        request: Omit<TranslateTextRequest, "traceData">
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.translateText({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as TranslateTextResponse;
    }

    public async translateSlide(
        request: Omit<TranslateSlideRequest, "traceData">
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.translateSlide({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as TranslateSlideResponse;
    }

    public async translatePresentation(
        request: Omit<TranslatePresentationRequest, "traceData">
    ) {
        const { response } = await this._handleApiErrors(() => aiApi.translatePresentation({
            ...request,
            workspaceId: app.appController.workspaceId
        }));

        return response as TranslatePresentationResponse;
    }
}

const aiGenerationService = new AiGenerationService();
export default aiGenerationService;
